import React, { useContext } from 'react';
import { EnterpriseMember } from 'models/Enterprises';
import { EnterpriseTeamMembersTableKeys } from './data';
import { useTranslation } from 'react-i18next';
import { getUserFullName, mapValueToMoneyFormat, removeLINK } from 'utils';
import usePastTime from 'hooks/usePastTime';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import useViewPortfolioAs from 'hooks/useViewPortfolioAs';
import { User } from 'models/User';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import { Button, Checkbox } from 'ncoded-component-library';
import PencilIcon from 'icons/Pencil.icon';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import {
  checkIsUserNotInvited,
  checkUserHasInvitationSent,
} from 'providers/EnterpriseMembers/EnterpriseMembers.provider';
import { formatPhoneNumber } from 'phoneNumberUtils';
import { BROKER_STATUS_TRANSLATION } from '../../../TeamFilters/data';

type CellProps = {
  member: EnterpriseMember;
};

const CheckboxCell: React.FC<CellProps> = ({ member }) => {
  const { checkIfMemberIsSelected, updateSelected } = useContext(
    EnterpriseMembersContext,
  );

  const isSelected = checkIfMemberIsSelected(member);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Checkbox checked={isSelected} onChange={() => updateSelected(member)} />
    </div>
  );
};

export const NameCell: React.FC<CellProps> = ({ member }) => {
  const {
    user,
    firstName: memberFirstName,
    lastName: memberLastName,
    profileImage,
  } = member || {};
  const {
    _id,
    avatarColor,
    profileImage: userProfileImage,
    firstName: userFirstName,
    lastName: userLastName,
  } = user || {};

  const firstName = userFirstName || memberFirstName;
  const lastName = userLastName || memberLastName;

  const fullName = getUserFullName(firstName, lastName);

  const imgUrl = profileImage?.url || userProfileImage?.url;

  const navigateToUsersPortfolio = useViewPortfolioAs();

  return (
    <div className="name-cell">
      <AvatarWithInitials
        user={
          {
            firstName,
            lastName,
            avatarColor,
            profileImage: { url: imgUrl },
          } as User
        }
      />
      <p
        style={{ textDecoration: 'underline' }}
        onClick={(e) => {
          e.stopPropagation();
          navigateToUsersPortfolio(_id, fullName);
        }}
      >
        {fullName}
      </p>
    </div>
  );
};

const EmailCell: React.FC<CellProps> = ({ member }) => {
  const { email } = member || {};

  if (!email) return null;

  return <p className="email-cell">{email}</p>;
};

const SeniorityCell: React.FC<CellProps> = ({ member }) => {
  const { seniority } = member;

  return <div className="seniority-cell">{seniority}</div>;
};

const UnitCell: React.FC<CellProps> = ({ member }) => {
  const { businessUnitName } = member;

  return (
    <p className="unit-cell">
      {businessUnitName ? removeLINK(businessUnitName) : '/'}
    </p>
  );
};

const StatusCell: React.FC<CellProps> = ({ member }) => {
  const { invitationStatus } = member;

  const { t } = useTranslation();

  return (
    <div className={classNames('status-cell', invitationStatus)}>
      {t(BROKER_STATUS_TRANSLATION[invitationStatus])}
    </div>
  );
};

const NotInvitedCell: React.FC<CellProps> = ({ member }) => {
  const { email, user } = member;

  const { t } = useTranslation();

  const formattedPhone = formatPhoneNumber(user?.phone);

  const { handleSendInvites } = useContext(EnterpriseMembersContext);

  return (
    <div className="not-invited-cell">
      {email && <p>{email}</p>}

      {formattedPhone && <p>{formattedPhone}</p>}

      <Button
        variant="outline"
        onClick={(e) => {
          e.stopPropagation();
          handleSendInvites([member]);
        }}
      >
        {t('sendInvite')}
      </Button>
    </div>
  );
};

const SentInvitationCell: React.FC<CellProps> = ({ member }) => {
  const { lastInviteSent } = member;

  const { t } = useTranslation();
  const date = usePastTime()(lastInviteSent);

  const { handleSendReminders } = useContext(EnterpriseMembersContext);

  return (
    <div className="sent-invitation-cell">
      {date && <p>{t('lastReminderSentDate', { date })}</p>}

      <Button
        variant="outline"
        onClick={(e) => {
          e.stopPropagation();
          handleSendReminders([member]);
        }}
      >
        {t('sendReminder')}
      </Button>
    </div>
  );
};

const DealsCell: React.FC<CellProps> = (props) => {
  const { member } = props;
  const { totalDeals } = member;

  const isPhablet = useMatchMedia('isPhablet');

  if (isPhablet) return <div>{totalDeals || '--'}</div>;

  return <div className="number-cell">{totalDeals}</div>;
};

const ListingsCell: React.FC<CellProps> = ({ member }) => {
  const { totalListings } = member;

  const isPhablet = useMatchMedia('isPhablet');

  if (isPhablet) return <div>{totalListings || '--'}</div>;

  return <div className="number-cell">{totalListings}</div>;
};

const ContactsCell: React.FC<CellProps> = ({ member }) => {
  const { totalContacts } = member;

  const isPhablet = useMatchMedia('isPhablet');

  if (isPhablet) return <div>{totalContacts || '--'}</div>;

  return <div className="number-cell">{totalContacts}</div>;
};

const SuccessFeeCell: React.FC<CellProps> = ({ member }) => {
  const { successFee } = member;

  return (
    <div className="commission-cell">
      {mapValueToMoneyFormat(successFee, true)}
    </div>
  );
};

const EvCell: React.FC<CellProps> = ({ member }) => {
  const { ev } = member;

  return <div className="ev-cell">{mapValueToMoneyFormat(ev, true)}</div>;
};

const LastSeenCell: React.FC<CellProps> = ({ member }) => {
  const { lastSeen } = member;

  const { t } = useTranslation();
  const pastTime = usePastTime()(lastSeen);

  if (!lastSeen) return t('na');

  return <div className="last-seen-cell">{pastTime}</div>;
};

const EditCell: React.FC<CellProps> = ({ member }) => {
  const { handleEditMember } = useContext(EnterpriseMembersContext);

  return (
    <div className="edit-cell">
      <Button
        variant="icon"
        icon={PencilIcon}
        onClick={(e) => {
          e.stopPropagation();
          handleEditMember(member);
        }}
      />
    </div>
  );
};

type BodyContentGenerate = Record<
  EnterpriseTeamMembersTableKeys,
  (cellProps: CellProps) => React.ReactNode
>;

export const BODY_CONTENT_GENERATE: BodyContentGenerate = {
  checkbox: (cellProps: CellProps) => <CheckboxCell {...cellProps} />,
  firstName: (cellProps: CellProps) => <NameCell {...cellProps} />,
  email: (cellProps: CellProps) => <EmailCell {...cellProps} />,
  seniority: (cellProps: CellProps) => <SeniorityCell {...cellProps} />,
  businessUnitName: (cellProps: CellProps) => <UnitCell {...cellProps} />,
  invitationStatus: (cellProps: CellProps) => <StatusCell {...cellProps} />,
  totalListings: (cellProps: CellProps) => <ListingsCell {...cellProps} />,
  totalContacts: (cellProps: CellProps) => <ContactsCell {...cellProps} />,
  successFee: (cellProps: CellProps) => <SuccessFeeCell {...cellProps} />,
  ev: (cellProps: CellProps) => <EvCell {...cellProps} />,
  lastSeen: (cellProps: CellProps) => <LastSeenCell {...cellProps} />,
  edit: (cellProps: CellProps) => <EditCell {...cellProps} />,
  totalDeals: ({ member }: CellProps) =>
    checkIsUserNotInvited(member.invitationStatus) ? (
      <NotInvitedCell member={member} />
    ) : checkUserHasInvitationSent(member.invitationStatus) ? (
      <SentInvitationCell member={member} />
    ) : (
      <DealsCell member={member} />
    ),
  totalDataRooms: ({ member }: CellProps) =>
    checkIsUserNotInvited(member.invitationStatus) ? (
      <NotInvitedCell member={member} />
    ) : checkUserHasInvitationSent(member.invitationStatus) ? (
      <SentInvitationCell member={member} />
    ) : (
      <DealsCell member={member} />
    ),
};
